import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { getRandomWord } from "./utils/wordList";

const StockWordle = ({ onClose }) => {
  const [solution] = useState(() => getRandomWord());
  const [guesses, setGuesses] = useState(Array(6).fill(null));
  const [currentGuess, setCurrentGuess] = useState("");
  const [currentRow, setCurrentRow] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  // Check if a letter is correct, present, or absent
  const checkLetter = (letter, position, word = solution) => {
    if (word[position] === letter) return "correct";
    if (word.includes(letter)) return "present";
    return "absent";
  };

  const onEnter = () => {
    if (currentGuess.length !== 5) return;

    // Add guess to board
    const newGuesses = [...guesses];
    newGuesses[currentRow] = currentGuess.toUpperCase();
    setGuesses(newGuesses);

    // Check if game is won or lost
    if (currentGuess.toUpperCase() === solution) {
      setGameOver(true);
    } else if (currentRow === 5) {
      setGameOver(true);
    }

    // Move to next row and clear current guess
    setCurrentRow(currentRow + 1);
    setCurrentGuess("");
  };

  const onKey = (value) => {
    if (gameOver) return;
    if (value === "ENTER") {
      onEnter();
    } else if (value === "BACKSPACE") {
      setCurrentGuess((prev) => prev.slice(0, -1));
    } else if (currentGuess.length < 5) {
      setCurrentGuess((prev) => prev + value);
    }
  };

  useEffect(() => {
    const listener = (e) => {
      if (e.key === "Enter") {
        onKey("ENTER");
      } else if (e.key === "Backspace") {
        onKey("BACKSPACE");
      } else if (/^[A-Za-z]$/.test(e.key)) {
        onKey(e.key.toUpperCase());
      }
    };

    window.addEventListener("keyup", listener);
    return () => window.removeEventListener("keyup", listener);
  }, [currentGuess, currentRow, gameOver]);

  // Render game board
  const renderBoard = () => {
    return (
      <div className="stockwordle-board">
        {guesses.map((guess, i) => (
          <div key={i} className="stockwordle-row">
            {Array(5)
              .fill(0)
              .map((_, j) => {
                const letter = guess
                  ? guess[j]
                  : i === currentRow
                  ? currentGuess[j]
                  : "";
                let status = "empty";

                if (guess) {
                  // Only evaluate completed guesses
                  status = checkLetter(guess[j], j);
                }

                return (
                  <div key={j} className={`stockwordle-tile ${status}`}>
                    {letter || ""}
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    );
  };

  // Render keyboard
  const keyboard = [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "BACKSPACE"],
  ];

  const renderKeyboard = () => (
    <div className="stockwordle-keyboard">
      {keyboard.map((row, i) => (
        <div key={i} className="keyboard-row">
          {row.map((key) => (
            <button
              key={key}
              className={`keyboard-button ${
                key.length > 1 ? "keyboard-button-wide" : ""
              }`}
              onClick={() => onKey(key)}
            >
              {key === "BACKSPACE" ? "←" : key}
            </button>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="game-modal-overlay">
      <div className="game-modal stockwordle-modal">
        <button className="game-close-button" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="stockwordle-container">
          <h2>StockWordle</h2>
          {renderBoard()}
          {renderKeyboard()}

          {gameOver && (
            <div className="game-over-message">
              {currentRow <= 6 && guesses[currentRow - 1] === solution
                ? "You won!"
                : `The word was ${solution}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockWordle;
